import { useState, useEffect, useCallback, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/utils/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Upload, FileArchive, Trash2, ArrowRight, RefreshCw } from "lucide-react";
import useDocumentTitle from "@/hooks/useDocumentTitle";

const statusColor = {
  pending: "bg-slate-100 text-slate-700 border-slate-200",
  extracting: "bg-blue-100 text-blue-700 border-blue-200",
  ready: "bg-amber-100 text-amber-700 border-amber-200",
  committing: "bg-purple-100 text-purple-700 border-purple-200",
  done: "bg-emerald-100 text-emerald-700 border-emerald-200",
  failed: "bg-red-100 text-red-700 border-red-200",
};

const fmtSize = (b) => {
  if (!b) return "";
  const mb = b / 1024 / 1024;
  return mb >= 1 ? `${mb.toFixed(1)} MB` : `${(b / 1024).toFixed(0)} KB`;
};

const fmtDate = (iso) => {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleString("en-GB", {
      day: "2-digit", month: "short", year: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  } catch { return iso; }
};

export default function ImportsPage() {
  useDocumentTitle("Bulk Imports", "Upload a ZIP of certificate PDFs; review before committing.");
  const navigate = useNavigate();

  const [batches, setBatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [file, setFile] = useState(null);
  const [defaultIssueDate, setDefaultIssueDate] = useState("");
  const [defaultExpiryDate, setDefaultExpiryDate] = useState("");
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const pollRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const r = await api.get("/import/v2/batches");
      setBatches(r.data.batches || []);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to load batches");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    // Auto-refresh while any batch is still extracting/committing
    pollRef.current = setInterval(() => {
      setBatches((prev) => {
        if (prev.some((b) => b.status === "extracting" || b.status === "committing")) {
          load();
        }
        return prev;
      });
    }, 4000);
    return () => clearInterval(pollRef.current);
  }, [load]);

  const upload = async () => {
    if (!file) return;
    setUploading(true);
    setUploadProgress(0);
    try {
      const fd = new FormData();
      fd.append("file", file);
      if (defaultIssueDate) fd.append("default_issue_date", defaultIssueDate);
      if (defaultExpiryDate) fd.append("default_expiry_date", defaultExpiryDate);
      const r = await api.post("/import/v2/certificates-zip", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress: (e) => {
          if (e.total) setUploadProgress(Math.round((e.loaded / e.total) * 100));
        },
      });
      toast.success(`Uploaded — extracting ${r.data.total} PDFs`);
      setUploadOpen(false);
      setFile(null);
      setDefaultIssueDate("");
      setDefaultExpiryDate("");
      setUploadProgress(0);
      navigate(`/imports/${r.data.batch_id}`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const remove = async (batchId) => {
    if (!confirm("Delete this batch and all its rows? Any certificates already imported will be kept.")) return;
    try {
      await api.delete(`/import/v2/batches/${batchId}`);
      toast.success("Batch deleted");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Delete failed");
    }
  };

  return (
    <div className="space-y-6" data-testid="imports-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight" data-testid="imports-title">Bulk Imports</h1>
          <p className="text-slate-500 text-sm mt-1">
            Upload a ZIP of certificate PDFs. We&apos;ll extract, match, and stage every row for review before creating any records.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={load} data-testid="imports-refresh-btn">
            <RefreshCw className="w-4 h-4 mr-2" />Refresh
          </Button>
          <Button onClick={() => setUploadOpen(true)} data-testid="imports-new-btn">
            <Upload className="w-4 h-4 mr-2" />New Import
          </Button>
        </div>
      </div>

      <div className="rounded-lg border bg-white shadow-sm overflow-hidden">
        {loading ? (
          <div className="p-8 text-center text-slate-500">Loading…</div>
        ) : batches.length === 0 ? (
          <div className="p-12 text-center text-slate-500">
            <FileArchive className="w-10 h-10 mx-auto mb-3 text-slate-300" />
            No imports yet. Click <b>New Import</b> to upload a ZIP.
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b text-left text-slate-600">
              <tr>
                <th className="px-4 py-3 font-medium">File</th>
                <th className="px-4 py-3 font-medium">Uploaded</th>
                <th className="px-4 py-3 font-medium">Status</th>
                <th className="px-4 py-3 font-medium text-right">Progress</th>
                <th className="px-4 py-3 font-medium text-right">Certs Created</th>
                <th className="px-4 py-3 font-medium text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {batches.map((b) => (
                <tr key={b.id} className="border-b hover:bg-slate-50" data-testid={`batch-row-${b.id}`}>
                  <td className="px-4 py-3">
                    <div className="font-medium">{b.filename}</div>
                    <div className="text-slate-500 text-xs">{fmtSize(b.size)} · uploaded by {b.uploaded_by}</div>
                  </td>
                  <td className="px-4 py-3 text-slate-600">{fmtDate(b.uploaded_at)}</td>
                  <td className="px-4 py-3">
                    <Badge variant="outline" className={statusColor[b.status] || "bg-slate-100"}>
                      {b.status}
                    </Badge>
                    {b.error && <div className="text-red-600 text-xs mt-1">{b.error}</div>}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <span className="tabular-nums">{b.extracted}/{b.total}</span>
                    {b.errored > 0 && <span className="text-red-600 text-xs ml-2">({b.errored} err)</span>}
                  </td>
                  <td className="px-4 py-3 text-right tabular-nums">
                    {b.created_certs > 0 ? (
                      <>
                        {b.created_certs}
                        {b.created_learners > 0 && (
                          <span className="text-slate-500 text-xs"> · +{b.created_learners} learners</span>
                        )}
                      </>
                    ) : (
                      <span className="text-slate-400">—</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="inline-flex items-center gap-1">
                      <Link to={`/imports/${b.id}`}>
                        <Button size="sm" variant="outline" data-testid={`batch-review-${b.id}`}>
                          Review<ArrowRight className="w-3 h-3 ml-1" />
                        </Button>
                      </Link>
                      <Button size="sm" variant="ghost" onClick={() => remove(b.id)} data-testid={`batch-delete-${b.id}`}>
                        <Trash2 className="w-4 h-4 text-red-500" />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <Dialog open={uploadOpen} onOpenChange={setUploadOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>New certificate import</DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-2">
            <div>
              <Label>ZIP file</Label>
              <Input
                type="file"
                accept=".zip,application/zip"
                onChange={(e) => setFile(e.target.files?.[0] || null)}
                data-testid="upload-file-input"
              />
              {file && (
                <div className="text-xs text-slate-500 mt-1">
                  {file.name} · {fmtSize(file.size)}
                </div>
              )}
            </div>

            <div className="rounded-lg bg-slate-50 border p-3 text-xs text-slate-600">
              <b>Filename convention we&apos;ll parse:</b>{" "}
              <code className="text-slate-800">FirstName LastName QUALCODE cert.pdf</code>
              <br />
              Certificate number, issue date and expiry date are extracted from the PDF content by vision OCR (GPT-5.2). Missing learners are auto-created on commit.
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Default issue date <span className="text-slate-400 font-normal">(fallback)</span></Label>
                <Input
                  type="date"
                  value={defaultIssueDate}
                  onChange={(e) => setDefaultIssueDate(e.target.value)}
                  data-testid="upload-default-issue"
                />
              </div>
              <div>
                <Label>Default expiry date <span className="text-slate-400 font-normal">(fallback)</span></Label>
                <Input
                  type="date"
                  value={defaultExpiryDate}
                  onChange={(e) => setDefaultExpiryDate(e.target.value)}
                  data-testid="upload-default-expiry"
                />
              </div>
            </div>
            <p className="text-xs text-slate-500 -mt-2">
              If vision OCR can&apos;t read a date from the PDF, we&apos;ll use these defaults. Expiry falls back to <i>issue date + qualification&apos;s validity years</i> if both are blank.
            </p>

            {uploading && (
              <div className="rounded-md border bg-blue-50 p-3 text-sm">
                Uploading… {uploadProgress}%
                <div className="w-full bg-blue-100 rounded-full h-1.5 mt-2">
                  <div className="bg-blue-500 h-1.5 rounded-full transition-all" style={{ width: `${uploadProgress}%` }} />
                </div>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setUploadOpen(false)} disabled={uploading}>Cancel</Button>
            <Button onClick={upload} disabled={!file || uploading} data-testid="upload-submit-btn">
              {uploading ? "Uploading…" : "Upload & Extract"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
